import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Circle, Clock3, Save, ShieldCheck, UserCheck } from 'lucide-react';
import * as api from '../services/api';
import ConfirmModal from './ConfirmModal';
import './AttendanceRecorder.css';

const today = () => new Date().toLocaleDateString('en-CA');
const initialForm = group => ({
    group,
    course: '',
    sessionNumber: 1,
    date: today(),
    startTime: '18:00',
    endTime: '20:00',
    notes: ''
});
const statusLabels = { present: 'حاضر', absent: 'غائب', late: 'متأخر', excused: 'بعذر' };
const statusIcons = {
    present: CheckCircle2,
    absent: Circle,
    late: Clock3,
    excused: ShieldCheck
};
const rowsFor = (students, attendance = []) => {
    const saved = new Map(attendance.map(row => [String(row.student?._id || row.student), row]));
    return students.map(student => {
        const row = saved.get(String(student._id));
        return { student: student._id, name: student.fullName, status: row?.status || 'absent', note: row?.note || '' };
    });
};

export default function AttendanceRecorder({ groups, onSaved }) {
    const [form, setForm] = useState(initialForm(''));
    const [setup, setSetup] = useState(null);
    const [records, setRecords] = useState([]);
    const [existingSession, setExistingSession] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmation, setConfirmation] = useState(false);
    const [savedSummary, setSavedSummary] = useState(null);

    const summary = useMemo(() => records.reduce((totals, row) => {
        totals.total += 1;
        totals[row.status] += 1;
        return totals;
    }, { total: 0, present: 0, absent: 0, late: 0, excused: 0 }), [records]);

    useEffect(() => {
        if (!form.group) {
            setSetup(null);
            setRecords([]);
            setExistingSession(null);
            return;
        }
        let active = true;
        setLoading(true);
        setError('');
        api.getAttendanceSetup(form.group)
            .then(data => {
                if (!active) return;
                setSetup(data);
                setRecords(rowsFor(data.students));
                setExistingSession(null);
                setSavedSummary(null);
                setForm(current => ({ ...initialForm(current.group), group: current.group }));
            })
            .catch(err => active && setError(err.message || 'تعذر تحميل بيانات المجموعة.'))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [form.group]);

    useEffect(() => {
        if (!setup || !form.course || !form.sessionNumber) return;
        const match = setup.sessions.find(session =>
            String(session.course) === String(form.course) && Number(session.sessionNumber) === Number(form.sessionNumber)
        );
        if (!match) {
            setExistingSession(null);
            setRecords(rowsFor(setup.students));
            setSavedSummary(null);
            setForm(current => ({ ...current, date: today(), startTime: '18:00', endTime: '20:00', notes: '' }));
            return;
        }
        if (existingSession?._id === match._id) return;
        let active = true;
        setLoading(true);
        setError('');
        api.getSessionAttendance(match._id)
            .then(data => {
                if (!active) return;
                setExistingSession(match);
                setRecords(rowsFor(setup.students, data.students.map(student => ({ student: student._id, ...student.attendance }))));
                setSavedSummary(data.students.some(student => student.attendance) ? 'loaded' : null);
                setForm(current => ({
                    ...current,
                    date: new Date(data.session.date).toISOString().slice(0, 10),
                    startTime: data.session.startTime,
                    endTime: data.session.endTime,
                    notes: data.session.notes || ''
                }));
            })
            .catch(err => active && setError(err.message || 'تعذر تحميل حضور الجلسة.'))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [setup, form.course, form.sessionNumber, existingSession?._id]);

    const changeGroup = value => setForm(initialForm(value));
    const updateRecord = (student, updates) => setRecords(current => current.map(row => row.student === student ? { ...row, ...updates } : row));
    const toggleStudent = student => setRecords(current => current.map(row => row.student === student
        ? { ...row, status: row.status === 'present' ? 'absent' : 'present' }
        : row));

    const requestSubmit = event => {
        event.preventDefault();
        setError('');
        if (!form.group || !form.course || !form.sessionNumber || !form.date || !form.startTime || !form.endTime) {
            setError('اختر المجموعة والكورس والجلسة والتاريخ ووقت البداية والنهاية.');
            return;
        }
        if (!records.length) {
            setError('لا يوجد طلاب حاليون لتسجيل حضورهم.');
            return;
        }
        setConfirmation(true);
    };

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            const sessionPayload = {
                sessionNumber: Number(form.sessionNumber),
                date: form.date,
                startTime: form.startTime,
                endTime: form.endTime,
                notes: form.notes
            };
            let session = existingSession;
            if (session) {
                session = await api.updateTeachingSession(session._id, sessionPayload);
            } else {
                session = await api.createTeachingSession({ ...sessionPayload, group: form.group, course: form.course });
                setExistingSession(session);
                setSetup(current => current ? { ...current, sessions: [...current.sessions, session] } : current);
            }
            await api.saveSessionAttendance(
                session._id,
                records.map(({ student, status, note }) => ({ student, status, note })),
                { completeSheet: true }
            );
            const refreshed = await api.getSessionAttendance(session._id);
            setExistingSession(session);
            setRecords(rowsFor(setup.students, refreshed.students.map(student => ({ student: student._id, ...student.attendance }))));
            setSavedSummary({ ...summary, sessionNumber: session.sessionNumber, group: setup.group.name });
            setConfirmation(false);
            if (onSaved) await onSaved();
        } catch (err) {
            setConfirmation(false);
            setError(err.message || 'تعذر إرسال الحضور.');
        } finally { setSaving(false); }
    };

    const confirmationText = setup
        ? `الجروب: ${setup.group.name} · الجلسة: ${form.sessionNumber} · إجمالي الطلاب: ${summary.total} · حاضر: ${summary.present} · غائب: ${summary.absent} · متأخر: ${summary.late} · بعذر: ${summary.excused}`
        : '';

    return <section className="aw-panel ar-recorder" aria-labelledby="attendance-recorder-title">
        <div className="aw-panel-head">
            <div><h2 id="attendance-recorder-title">تسجيل الحضور</h2><p>اختر الجلسة، ثم اضغط على أسماء الطلاب لتسجيل الحضور بسرعة.</p></div>
            {existingSession && <span className="ar-edit-badge">وضع تعديل</span>}
        </div>

        {error && <div className="ar-message error" role="alert">{error}</div>}
        {savedSummary && savedSummary !== 'loaded' && <div className="ar-message success" role="status"><UserCheck size={18} /> تم تسجيل الحضور بنجاح — {savedSummary.group}، الجلسة {savedSummary.sessionNumber}: حاضر {savedSummary.present}، غائب {savedSummary.absent}، متأخر {savedSummary.late}، بعذر {savedSummary.excused}.</div>}
        {savedSummary === 'loaded' && <div className="ar-message info" role="status">تم تحميل الحضور المسجل. يمكنك تعديل الحالات ثم حفظ التعديلات.</div>}

        <form onSubmit={requestSubmit}>
            <div className="ar-session-fields">
                <label><span>المجموعة</span><select aria-label="المجموعة" value={form.group} onChange={event => changeGroup(event.target.value)} required><option value="">اختر المجموعة</option>{groups.map(group => <option key={group._id} value={group._id}>{group.name}</option>)}</select></label>
                <label><span>الكورس</span><select aria-label="الكورس" value={form.course} onChange={event => setForm({ ...form, course: event.target.value })} disabled={!setup || loading} required><option value="">اختر الكورس</option>{(setup?.courses || []).map(course => <option key={course._id} value={course._id}>{course.title}</option>)}</select></label>
                <label><span>رقم الجلسة</span><select aria-label="رقم الجلسة" value={form.sessionNumber} onChange={event => setForm({ ...form, sessionNumber: Number(event.target.value) })} disabled={!form.course} required>{Array.from({ length: Math.min(setup?.group?.sessionLimit || 12, 12) }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}</select></label>
                <label><span>التاريخ</span><input aria-label="التاريخ" type="date" value={form.date} onChange={event => setForm({ ...form, date: event.target.value })} required /></label>
                <label><span>وقت البداية</span><input aria-label="وقت البداية" type="time" value={form.startTime} onChange={event => setForm({ ...form, startTime: event.target.value })} required /></label>
                <label><span>وقت النهاية</span><input aria-label="وقت النهاية" type="time" value={form.endTime} onChange={event => setForm({ ...form, endTime: event.target.value })} required /></label>
            </div>

            {loading && <div className="ar-loading"><span className="aw-spinner" /> جارٍ تحميل الطلاب والجلسات...</div>}
            {!loading && setup && !setup.courses.length && <div className="ar-message info">لا توجد كورسات نشطة مرتبطة بهذه المجموعة والمدرب.</div>}
            {!loading && setup && setup.courses.length > 0 && !setup.students.length && <div className="ar-message info">لا يوجد طلاب حاليون نشطون في هذه المجموعة.</div>}

            {!loading && records.length > 0 && <>
                <div className="ar-student-heading"><h3>طلاب المجموعة</h3><span>الضغط على الاسم يبدّل بين حاضر وغائب</span></div>
                <div className="ar-student-grid">
                    {records.map(row => {
                        const StatusIcon = statusIcons[row.status];
                        return <article key={row.student} className={`ar-student-card ${row.status}`}>
                            <button type="button" className="ar-student-toggle" onClick={() => toggleStudent(row.student)} aria-pressed={row.status === 'present'} aria-label={`${row.name} — ${statusLabels[row.status]}`}>
                                <StatusIcon size={22} aria-hidden="true" />
                                <span><b>{row.name}</b><small>{statusLabels[row.status]}</small></span>
                            </button>
                            <label><span className="sr-only">حالة {row.name}</span><select aria-label={`حالة ${row.name}`} value={row.status} onChange={event => updateRecord(row.student, { status: event.target.value })}><option value="present">حاضر</option><option value="absent">غائب</option><option value="late">متأخر</option><option value="excused">بعذر</option></select></label>
                            <input aria-label={`ملاحظة ${row.name}`} placeholder="ملاحظة اختيارية" maxLength="500" value={row.note} onChange={event => updateRecord(row.student, { note: event.target.value })} />
                        </article>;
                    })}
                </div>

                <label className="ar-session-notes"><span>ملاحظات الجلسة</span><textarea aria-label="ملاحظات الجلسة" rows="3" maxLength="2000" placeholder="مثال: تم مراجعة loops وتم حل التمرين حتى السؤال 5." value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} /></label>
                <div className="ar-live-summary" aria-live="polite"><span>الإجمالي <b>{summary.total}</b></span><span>حاضر <b>{summary.present}</b></span><span>غائب <b>{summary.absent}</b></span><span>متأخر <b>{summary.late}</b></span><span>بعذر <b>{summary.excused}</b></span></div>
                <button className="aw-button ar-submit" disabled={saving}><Save size={18} /> {saving ? 'جارٍ الحفظ...' : existingSession ? 'حفظ التعديلات' : 'إرسال الحضور'}</button>
            </>}
        </form>

        <ConfirmModal isOpen={confirmation} title="هل أنت متأكد من إرسال الحضور؟" message={confirmationText} onCancel={() => setConfirmation(false)} onConfirm={submit} confirmText="تأكيد وإرسال" cancelText="إلغاء" type="info" />
    </section>;
}
